/**
 * Открытие учебного примера (E4.6a): шаг урока, задание или практика открываются в
 * учебном экземпляре пользователя. В браузере нет сохранённого -- продолжается последний
 * пример пользователя на сервере, а если его нет -- создаётся новый и запоминается в
 * прогрессе. Сохранённый экземпляр недоступен (удалён, истёк, чужой) -- новый не
 * создаётся сам: пользователь явно выбирает «Новый учебный пример». Запуски всех потребителей
 * (страница обучения, тур, задание) делят один признак занятости и один идущий запрос примера:
 * двойной клик не создаёт два экземпляра.
 */

import { useCallback, useState } from 'react';
import { create } from 'zustand';
import { ApiError } from '@/api/client';
import {
	createTrainingInstance,
	getTrainingInstance,
	listTrainingInstances,
	type TrainingManifest,
} from '@/api/training';
import { useWorkspaceDataSource } from '@/features/workspace/WorkspaceDataSourceContext';
import { setActiveInstance, startNewExample } from './progress';
import { useTrainingProgress } from './useTrainingProgress';

/** Почему пример не открылся: экземпляр недоступен или запрос не удался */
export type LaunchFailure = 'instance_unavailable' | 'failed';

/** Число идущих запусков всех потребителей */
const useLaunchStore = create<{ running: number }>(() => ({ running: 0 }));

/** Идущий запрос примера: тот же активный экземпляр -- тот же запрос */
let pending: { key: string | null; manifest: Promise<TrainingManifest | null> } | null = null;

function track<T>(work: () => Promise<T>): Promise<T> {
	useLaunchStore.setState((state) => ({ running: state.running + 1 }));
	return work().finally(() => useLaunchStore.setState((state) => ({ running: state.running - 1 })));
}

export function useTrainingLauncher() {
	const { mode, manifest: openManifest, enterTraining } = useWorkspaceDataSource();
	const { progress, update } = useTrainingProgress();
	const [failure, setFailure] = useState<LaunchFailure | null>(null);
	const busy = useLaunchStore((state) => state.running > 0);

	const load = useCallback(
		async (active: string | null): Promise<TrainingManifest | null> => {
			if (active === null) {
				/* На новом устройстве продолжается последний пример пользователя; нет примера -- новый */
				const latest = (await listTrainingInstances()).instances[0];
				const manifest = latest
					? (await getTrainingInstance(latest.instance_id)).manifest
					: await createTrainingInstance(crypto.randomUUID());
				update((current) => setActiveInstance(current, manifest.instance_id));
				return manifest;
			}
			try {
				return (await getTrainingInstance(active)).manifest;
			} catch (error) {
				if (error instanceof ApiError && error.status === 404) return null;
				throw error;
			}
		},
		[update],
	);

	const manifestOf = useCallback((): Promise<TrainingManifest | null> => {
		const active = progress.active_instance_id;
		if (mode !== 'live' && openManifest && openManifest.instance_id === active)
			return Promise.resolve(openManifest);
		if (pending !== null && pending.key === active) return pending.manifest;
		const manifest = track(() => load(active)).finally(() => {
			if (pending?.manifest === manifest) pending = null;
		});
		pending = { key: active, manifest };
		return manifest;
	}, [load, mode, openManifest, progress.active_instance_id]);

	const run = useCallback(async (action: () => Promise<void>) => {
		setFailure(null);
		try {
			await track(action);
		} catch {
			setFailure('failed');
		}
	}, []);

	/** Открыть адрес учебного экземпляра: route строится из его manifest */
	const open = useCallback(
		(route: (manifest: TrainingManifest) => string) =>
			run(async () => {
				const manifest = await manifestOf();
				if (manifest === null) {
					setFailure('instance_unavailable');
					return;
				}
				enterTraining(manifest.instance_id, route(manifest));
			}),
		[enterTraining, manifestOf, run],
	);

	/** Новый учебный пример становится активным: уроки сохраняются, незавершённые задания -- с начала */
	const createExample = useCallback(
		() =>
			track(async () => {
				const created = await createTrainingInstance(crypto.randomUUID());
				update((current) => startNewExample(current, created.instance_id));
				return created;
			}),
		[update],
	);

	const newExample = useCallback(
		() =>
			run(async () => {
				await createExample();
			}),
		[createExample, run],
	);

	return { open, newExample, manifestOf, busy, failure };
}
